import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

const milestones = [
  { year: '2018', title: '品牌创立', desc: '麻豆cm传媒正式成立，致力于打造高品质视频内容平台。' },
  { year: '2019', title: '平台上线', desc: '麻豆cm传媒在线高清平台正式发布，提供1080P高清视频内容。' },
  { year: '2020', title: '4K升级', desc: '全面支持4K超高清画质，用户体验大幅提升。' },
  { year: '2021', title: '千万用户', desc: '平台注册用户突破1000万，成为行业领先的视频平台。' },
  { year: '2023', title: '内容翻倍', desc: '高清视频资源库突破5万部，覆盖全品类视频内容。' },
  { year: '2025', title: '五千万用户', desc: '活跃用户突破5000万，稳居高清视频平台第一梯队。' },
  { year: '2026', title: 'AI赋能', desc: '引入AI智能推荐系统，为用户提供更精准的内容推荐。' },
];

const breadcrumbItems = [
  { name: '首页', path: '/' },
  { name: '关于我们' },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const hpValue = (formData.get('phone_alt') as string || '').trim();
    if (hpValue) {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9rihmee3oq4jqumu1q0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries()).filter(([k]) => k !== 'phone_alt').map(([k, v]) => [k, v.toString()])
        ).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormError('提交失败，请稍后再试。');
          setFormStatus('error');
        } else {
          setFormError(serverMsg || '提交失败，请稍后再试。');
          setFormStatus('error');
        }
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试。');
      setFormStatus('error');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '麻豆cm传媒在线高清',
    url: 'https://madoucm.com/',
    description: '麻豆cm传媒在线高清为您提供最新高清视频内容，画质清晰流畅，海量资源免费在线观看。',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-400-888-9999',
      contactType: 'customer service',
      availableLanguage: 'Chinese',
    },
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 麻豆cm传媒在线高清',
    description: '了解麻豆cm传媒在线高清的品牌故事、发展历程和联系方式。',
    url: 'https://madoucm.com/about',
  };

  return (
    <>
      <title>关于我们 - 麻豆cm传媒在线高清</title>
      <meta name="description" content="了解麻豆cm传媒在线高清的品牌故事与发展历程。作为领先的高清视频平台，我们致力于为用户提供最佳的在线观影体验。查看我们的联系方式。" />
      <meta name="keywords" content="麻豆cm传媒在线高清,关于我们,品牌介绍,联系方式,发展历程" />
      <link rel="canonical" href="https://madoucm.com/about" />
      <meta property="og:title" content="关于我们 - 麻豆cm传媒在线高清" />
      <meta property="og:description" content="了解麻豆cm传媒在线高清的品牌故事与发展历程。" />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://madoucm.com/about" />
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>

      <Navbar />
      <main className="pt-20 pb-14 bg-background-50 min-h-screen">
        <div className="max-w-[1100px] mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          {/* Brand Intro */}
          <section className="mb-16">
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">
              关于麻豆cm传媒
            </h1>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed max-w-[800px]">
              麻豆cm传媒在线高清成立于2018年，是国内领先的高清视频内容平台。我们始终秉持"高清画质、流畅体验、海量资源"的理念，为广大用户提供最新、最全的高清视频内容。经过多年发展，平台已积累超过10万部高清视频资源，涵盖动作、剧情、喜剧、科幻、纪录片、动漫、综艺等多个品类，满足不同用户的观影需求。
            </p>
          </section>

          {/* Mission */}
          <section className="mb-16 bg-background-100 rounded-2xl p-8 md:p-10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-eye-line text-2xl"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 mb-2">极致画质</h3>
                <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">支持4K超高清播放，自适应码率确保流畅体验</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                  <i className="ri-stack-line text-2xl"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 mb-2">海量资源</h3>
                <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">超过10万部高清视频，每天持续更新最新内容</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                  <i className="ri-device-line text-2xl"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 mb-2">多端同步</h3>
                <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">电脑、平板、手机全终端覆盖，随时随地畅享高清</p>
              </div>
            </div>
          </section>

          {/* Milestones */}
          <section className="mb-16">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8 text-center">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-1/2 top-0 bottom-0 w-px bg-background-300 hidden md:block"></div>
              <div className="flex flex-col gap-6">
                {milestones.map((m, i) => (
                  <div key={m.year} className={`flex flex-col md:flex-row items-start gap-4 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className={`flex-1 ${i % 2 === 0 ? 'md:text-right' : 'md:text-left'} hidden md:block`}>
                      {i % 2 === 0 ? (
                        <div className="bg-background-100 rounded-xl p-5">
                          <h3 className="font-heading font-semibold text-foreground-900 text-sm">{m.title}</h3>
                          <p className="text-xs text-foreground-500 mt-1">{m.desc}</p>
                        </div>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-3 md:flex-col md:gap-1">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-500 text-background-50 font-heading font-bold text-sm flex-shrink-0">
                        {m.year}
                      </div>
                      <span className="md:hidden font-heading font-semibold text-foreground-900 text-sm">{m.title}</span>
                    </div>
                    <div className={`flex-1 ${i % 2 === 0 ? 'md:text-left' : 'md:text-right'} ${i % 2 !== 0 ? 'hidden md:block' : ''}`}>
                      {i % 2 !== 0 ? (
                        <div className="bg-background-100 rounded-xl p-5">
                          <h3 className="font-heading font-semibold text-foreground-900 text-sm">{m.title}</h3>
                          <p className="text-xs text-foreground-500 mt-1">{m.desc}</p>
                        </div>
                      ) : null}
                    </div>
                    <div className={`md:hidden ${i % 2 === 0 ? '' : 'ml-13'}`}>
                      <p className="text-xs text-foreground-500">{m.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="mb-16">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { value: '10万+', label: '高清视频资源' },
                { value: '5000万+', label: '活跃用户' },
                { value: '4K', label: '超高清画质' },
                { value: '7×24', label: '全天候服务' },
              ].map((stat) => (
                <div key={stat.label} className="bg-background-100 rounded-xl p-5 text-center">
                  <p className="text-2xl md:text-3xl font-heading font-bold text-primary-500">{stat.value}</p>
                  <p className="text-xs text-foreground-500 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="bg-background-100 rounded-2xl p-8 md:p-10" id="contact">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-500 mb-6 leading-relaxed">
                  如果您有任何问题或建议，欢迎通过以下方式联系我们，或填写右侧表单，我们将尽快回复。
                </p>
                <div className="flex flex-col gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                      <p className="text-sm text-foreground-500">contact@madoucm.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-phone-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">客服热线</p>
                      <p className="text-sm text-foreground-500">400-888-9999</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 flex-shrink-0">
                      <i className="ri-time-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">工作时间</p>
                      <p className="text-sm text-foreground-500">周一至周日 9:00 - 21:00</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <form
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-4"
                >
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-1.5">姓名</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-1.5">邮箱</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-1.5">留言内容</label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言内容（不超过500字）"
                    ></textarea>
                  </div>
                  <input
                    type="text"
                    name="phone_alt"
                    className="hp-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                  <button
                    type="submit"
                    className="bg-primary-500 hover:bg-primary-600 text-background-50 font-medium px-6 py-2.5 rounded-full text-sm whitespace-nowrap transition-all cursor-pointer inline-flex items-center justify-center gap-2"
                  >
                    <i className="ri-send-plane-line"></i> 发送消息
                  </button>
                  {formStatus === 'success' && (
                    <p className="text-sm text-green-600 bg-green-50 px-4 py-2 rounded-lg">消息已发送成功，我们会尽快与您联系！</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-600 bg-red-50 px-4 py-2 rounded-lg">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}